import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useSeederOrders from "../../services/useSeederOrders";
import useDeleteSeederOrder from "../../services/useDeleteSeederOrder";
import { ISeederOrder } from "../../types/seederOrders";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";

dayjs.locale("pt-br");

const SeederOrders = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useSeederOrders();
  const deleteOrderMutation = useDeleteSeederOrder();

  const [selectedOrder, setSelectedOrder] = useState<ISeederOrder | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<number | null>(null);

  const handleViewDetails = (order: ISeederOrder) => {
    setSelectedOrder(order);
    setShowDetailsDialog(true);
  };

  const handleDeleteOrder = (orderId: number) => {
    deleteOrderMutation.mutate(orderId, {
      onSuccess: () => {
        setOrderToDelete(null);
      },
      onError: (err) => {
        console.error("Erro ao deletar pedido:", err);
      },
    });
  };

  const formatCurrency = (value: string) => {
    return parseFloat(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("DD/MM/YYYY");
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Erro ao carregar pedidos. Tente novamente.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" component="h1">
          Pedidos
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate("/app/create-order")}
        >
          Novo Pedido
        </Button>
      </Box>

      <Card>
        <CardContent>
          {!data || data.results.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Nenhum pedido encontrado.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate("/app/create-order")}
                sx={{ mt: 2 }}
              >
                Criar Primeiro Pedido
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Parceira Urbana</TableCell>
                    <TableCell>Data do Pedido</TableCell>
                    <TableCell>Data de Entrega</TableCell>
                    <TableCell align="right">Itens</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="right">Taxa de Entrega</TableCell>
                    <TableCell align="right">Desconto</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.results.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>#{order.id}</TableCell>
                      <TableCell>{order.urban_partner_name}</TableCell>
                      <TableCell>{formatDate(order.date)}</TableCell>
                      <TableCell>{formatDate(order.delivery_date)}</TableCell>
                      <TableCell align="right">
                        <Chip label={order.items_count} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(order.subtotal)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(order.delivery_fee)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(order.discount) > 0 ? (
                          <Typography color="success.main">
                            -{formatCurrency(order.discount)}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {formatCurrency(order.total)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(order)}
                          title="Ver detalhes"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setOrderToDelete(order.id)}
                          title="Deletar"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Detalhes do Pedido */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalhes do Pedido #{selectedOrder?.id}</DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Parceira Urbana
                </Typography>
                <Typography variant="body1">
                  {selectedOrder.urban_partner_name}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Endereço de Entrega
                </Typography>
                <Typography variant="body1">{selectedOrder.address}</Typography>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Itens do Pedido
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Produto</TableCell>
                        <TableCell>Produtor</TableCell>
                        <TableCell align="right">Qtd</TableCell>
                        <TableCell align="right">Preço Unit.</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items_detail.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell>{item.producer_name}</TableCell>
                          <TableCell align="right">{item.amount}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.price)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Subtotal:</Typography>
                  <Typography>
                    {formatCurrency(selectedOrder.subtotal)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Taxa de Entrega:</Typography>
                  <Typography>
                    {formatCurrency(selectedOrder.delivery_fee)}
                  </Typography>
                </Box>
                {parseFloat(selectedOrder.discount) > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography>Desconto:</Typography>
                    <Typography color="success.main">
                      -{formatCurrency(selectedOrder.discount)}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                    pt: 1,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography fontWeight="bold">Total:</Typography>
                  <Typography fontWeight="bold">
                    {formatCurrency(selectedOrder.total)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog
        open={orderToDelete !== null}
        onClose={() => setOrderToDelete(null)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o pedido #{orderToDelete}?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderToDelete(null)}>Cancelar</Button>
          <Button
            onClick={() => orderToDelete && handleDeleteOrder(orderToDelete)}
            color="error"
            variant="contained"
            disabled={deleteOrderMutation.isLoading}
          >
            {deleteOrderMutation.isLoading ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SeederOrders;
